import React, { useRef, useState } from "react";
import SetPasswordFields from "../fields/set-password";
import setPasswordValidation from "../../../validation/setPassword";
import { Box, Button, Checkbox, Divider, FormControlLabel, Grid, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Formik } from "formik";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { useSetPassword } from "../../../api/auth";
import { showErrorAlert, showSuccessAlert } from "../alerts";
import Header from "../header";
import { tokens } from "../../layout/theme";


/**
 * SetPasswordForm Component
 * 
 * A form component for setting a new password.
 * Provides password confirmation and validation.
 * 
 * @component
 * @returns {JSX.Element} The rendered set password form
 * 
 * @example
 * ```tsx
 * <SetPasswordForm />
 * ```
 * 
 * @remarks
 * - Uses Formik for form state management and validation
 * - Integrates with Material-UI components for consistent styling
 * - Provides responsive design for mobile and desktop
 * - Includes password confirmation field
 * - Handles form submission with proper error handling
 * - Navigates to login page on success
 */
const SetPasswordForm = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const setPasswordMutation = useSetPassword();
  const navigate: NavigateFunction = useNavigate();

  // Tracks whether the user has scrolled to the bottom of the AGB text
  const [agbScrolled, setAgbScrolled] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const initialValues = {
    email: "",
    password: "",
    password_confirmation: "",
    agbAccepted: false, // Added for AGB acceptance tracking
  };

  /**
   * Handles the scroll event on the AGB text box.
   * Unlocks the checkbox once the user has reached the bottom.
   */
  const handleAgbScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
    if (atBottom) setAgbScrolled(true);
  };

  /**
   * Handles the form submission for setting a new password.
   * 
   * This function processes the form data and sends it to the server
   * to set a new password. It handles success and error cases
   * appropriately with user notifications.
   * 
   * @param {any} values - The form values to be submitted
   * @returns {Promise<void>} A promise that resolves when the submission is complete
   * 
   * @throws {Error} If the API request fails, an error is thrown with the response data
   * 
   * @example
   * ```tsx
   * const values = {
   *   email: "user@example.com",
   *   password: "newPassword123",
   *   password_confirmation: "newPassword123"
   * };
   * await handleFormSubmit(values);
   * ```
   */
  const handleFormSubmit = async (values: any) => {
    try {
      const { email, password, password_confirmation } = values;

      await setPasswordMutation.mutateAsync({
        email,
        password,
        password_confirmation,
      });

      await showSuccessAlert(`Das Passwort wurde erfolgreich festgelegt!`);

      navigate(`/login`);
    } catch (error: any) {
      const { data } = error.response;
      showErrorAlert(data.message);
    }
  };

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={setPasswordValidation}
      validateOnBlur={false}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
      }) => (
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4} alignItems="stretch">

            {/* Left column — password fields and submit button */}
            <Grid item xs={12} md={4}>
               <Box maxWidth="450px" >
                <Header
            showLogo
            title="careblue controllcenter"
            subtitle="Bitte bestätigen Sie Ihr Konto"
          />
              <SetPasswordFields
                isNonMobile={isNonMobile}
                values={values}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur}
                handleChange={handleChange}
              />
              <Box display="flex" justifyContent="end" m="15px" >
                <Button
                  size="medium"
                  type="submit"
                  color="secondary"
                  variant="contained"
                  sx={{
                    fontSize: "1rem",
                    textTransform: "none",
                  }}
                  // Button disabled if submitting or AGB not checked
                  disabled={isSubmitting || !values.agbAccepted}
                >
                  {isSubmitting ? `Bestätigen....` : `Bestätigen`}
                </Button>
                </Box>
              </Box>
            </Grid>

            {/* Right column — scrollable AGB text and acceptance checkbox */}
            <Grid item xs={12} md={8}>
                  <Box m="15px" display="flex" flexDirection="column" height="100%" >
                <Typography variant="h3" mb="8px">
                  Bitte lesen Sie die Nutzungsbedingungen sorgfältig durch.
                </Typography>
                <Paper
                  variant="outlined"
                  ref={scrollRef}
                  onScroll={handleAgbScroll}
sx={{
  maxHeight: "60vh", // takes up to 60% of the screen height
  minHeight: "200px", // never gets too small on small screens
  overflowY: "scroll",
  padding: "16px",
  fontSize: "0.85rem",
  lineHeight: 1.7,
  backgroundColor: colors.primary[400],
}}
>

<Typography variant="h5" gutterBottom fontStyle="italic" fontWeight="bold">
  Allgemeine Geschäftsbedingungen für die Bereitstellung des careblue control centers
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  1. Geltungsbereich und Vertragsgegenstand
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>1.1</strong> Diese Allgemeinen Geschäftsbedingungen („<strong>AGB</strong>") gelten für die Bereitstellung des web- und cloudbasierten
  Wasserdatenmanagementsystems „careblue control center" (im Folgenden „<strong>C3</strong>") durch die AQUA-CONCEPT
  Gesellschaft für Wasserbehandlung mbH (im Folgenden: „<strong>Aqua-Concept</strong>") gegenüber dem jeweiligen Kunden
  (nachfolgend „<strong>Servicenehmer</strong>"), mit welchem über ein gesondertes Bestelldokument (im Folgenden
  „<strong>Bestellformular</strong>") ein entsprechender Bereitstellungsvertrag abgeschlossen wurde (Aqua-Concept und
  Servicenehmer im Folgenden einzeln auch „<strong>Partei</strong>" gemeinsam „<strong>Parteien</strong>").
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>1.2</strong> Das Bestellformular, einschließlich dessen Anlagen, und diese AGB sowie ggf. alle weiteren zwischen den Parteien
  im Zusammenhang mit der Bereitstellung von C3 abgeschlossenen Vereinbarungen bilden gemeinsam den „<strong>C3-Service-Vertrag</strong>".
  Im Falle von Widersprüchen gehen Vereinbarungen im Bestellformular, einschließlich dessen Anlagen, diesen AGB vor.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>1.3</strong> Aqua-Concept stellt dem Servicenehmer C3 für die Dauer der Vertragslaufzeit und gegen Vergütung als
  Software-as-a-Service-(„SaaS") Plattform bereit. C3 dient der zentralen Verwaltung technischer und betrieblicher
  Wasserdaten in Bezug auf die in dem Bestellformular näher bezeichneten betrieblichen Einrichtungen, Anlagen
  und Datenbestände des Servicenehmers.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>1.4</strong> Der Servicenehmer erhält von Aqua-Concept einen einheitlichen Kundenzugang für den Zugriff auf C3 durch eine
  unbegrenzte Anzahl an Berechtigten Nutzern. „<strong>Berechtigte Nutzer</strong>" sind (1.) alle Mitarbeiter des Servicenehmers
  und (2.) Mitarbeiter von Dienstleistern des Servicenehmers und verbundenen Unternehmen des Servicenehmers
  im Sinne der §§ 15 ff. Aktiengesetz („AktG"), für deren bestimmungsgemäße Tätigkeit eine Mitbenutzung von C3
  erforderlich ist. Zusätzliche Kundenzugänge sind im Bestellformular gesondert zu vereinbaren und
  vergütungspflichtig. Auf Anfrage des Servicenehmers mit angemessener Vorlaufzeit kann Aqua-Concept etwaigen
  Dritten (insbesondere Sachverständigen oder Behörden) zeitlich beschränkte Leserechte für C3 einstellen. Über
  Einzelheiten werden sich die Parteien gesondert einigen.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>1.5</strong> Berechtigte Nutzer und / oder Aqua-Concept im Auftrag des Servicenehmers können Wasserdaten sowie
  Messungen, Probeergebnisse, o.Ä. in Bezug auf die in dem Bestellformular näher bezeichneten betrieblichen
  Einrichtungen, Anlagen und Datenbestände des Servicenehmers in C3 eingeben (nachfolgend gemeinsam
  „<strong>Servicenehmerdaten</strong>" genannt). Die Berechtigten Nutzer sind für die von ihnen eingegebenen
  Servicenehmerdaten bis zum Moment der Eingabe in C3 (inklusive) allein verantwortlich.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>1.6</strong> Sofern ausdrücklich vereinbart, kann die Eingabe von Servicenehmerdaten auch automatisch durch von
  Aqua-Concept bereitgestellte Sensoren erfolgen (Internet of Things). Einzelheiten in Bezug auf die Bereitstellung und
  Nutzung von Sensoren legen die Parteien ggf. gesondert fest.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>1.7</strong> C3 ermöglicht dem Servicenehmer und Aqua-Concept das systematische Abrufen und Monitoring der
  Servicenehmerdaten in gebündelter und übersichtlicher Form, auch mittels Browserzugang über mobile Endgeräte
  (Smartphone, etc.). Anhand der Servicenehmerdaten ist C3 in der Lage, u.a. automatische Berichte und Analysen
  zu generieren (etwa im Zusammenhang mit Vor-Ort Analysen, chemische und mikrobiologische Laborberichte,
  Störungsmeldungen, Wartungen/Inspektionen, Besuche von Aqua-Concept beim Servicenehmer). Ergeben sich
  aus den Servicenehmerdaten relevante Grenzwertüberschreitungen, kann C3 vorab vereinbarte Warnungen
  ausgeben und Maßnahmenvorschläge unterbreiten (Berichte, Analysen, Warnungen, Maßnahmenvorschläge
  oder sonstige von C3 generierte Ergebnisse nachfolgend „Ausgaben" genannt). Im Rahmen dieses C3-Service-
  Vertrages über die Bereitstellung von C3 schuldet Aqua-Concept nicht die Lösung oder Bearbeitung von über C3
  festgestellten Problemen / Störungen; dies kann jedoch Gegenstand eines gesonderten Vertrages zwischen den
  Parteien über Pflege- und Wartungsleistungen sein.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>1.8</strong> Wenn und soweit Aqua-Concept zeitgleich zur Bereitstellung von C3 gemäß diesem C3-Service-Vertrag auch
  Pflege- und Wartungsleistungen aufgrund eines gesonderten Vertrages mit dem Servicenehmer schuldet, kann
  der Servicenehmer eine Erfüllung der Pflege- und Wartungsleistungen durch Aqua-Concept nur soweit in digitaler
  Form über C3 verlangen, soweit dies ausdrücklich zwischen den Parteien vereinbart und technisch möglich ist.
  Ohne ausdrückliche Vereinbarung steht es Aqua-Concept jederzeit frei nach eigenem Ermessen aber unter
  Berücksichtigung der Interessen des Servicenehmers für jede Leistungserfüllung zu entscheiden, seine
  Leistungspflichten (bspw.: Erstellung von Analyseberichten) ganz oder teilweise analog oder mittels der Funktionen
  von C3 in digitaler Form zu erfüllen (§ 362 BGB). Umgekehrt kann Aqua-Concept im Rahmen der Bereitstellung
  von C3 geschuldete digitale Funktionalitäten (bspw.: digitale Erstellung von Analysen) ersatzweise auch in
  analoger Form erfüllen (§ 362 BGB), soweit nicht im Einzelnen anders vereinbart und soweit dies technisch
  möglich ist.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>1.9</strong> Soweit der Servicenehmer mit Aqua-Concept einen Regelservicevertrag über Pflege- und Wartungsleistungen
  geschlossen hat, hat der Regelservicevertrag die Funktion eines Rahmenvertrages und wird durch diesen C3-
  Service-Vertrag als Einzelvertrag ergänzt. Dieser C3-Service-Vertrag wird ab dem Moment, in dem beide Verträge
  in Kraft sind, Bestandteil des Regelservicevertrages, so dass die Regelungen des Regelservicevertrages nach den
  dort vereinbarten Regeln Anwendung finden, sofern nicht in diesem C3-Service-Vertrag etwas anderes geregelt
  ist (Vorrang des Einzelvertrages im Konfliktfall).
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  2. Gebühren und Zahlungsbedingungen
</Typography>

<Typography variant="body2" gutterBottom>
  Die Gebühren und Zahlungsbedingungen für die Bereitstellung von C3 ergeben sich aus dem Bestellformular.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  3. Leistungsbeschaffenheit, SLA
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>3.1</strong> Aqua-Concept stellt C3 gemäß der Vereinbarung in diesem C3-Service-Vertrag bereit. Öffentliche Äußerungen, z.
  B. Werbeaussagen von Aqua-Concept, Subunternehmern, Lieferanten oder Dritten zählen dabei nicht zur
  vertraglich vereinbarten Beschaffenheit von C3. Der Servicenehmer erkennt an, dass C3 einer stetigen
  Weiterentwicklung durch Aqua-Concept und regelmäßigen Software-Updates unterliegt.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>3.2</strong> Die Parteien regeln die Verfügbarkeit von C3 und, sofern vereinbart, den technischen Support in einem oder
  mehreren Service Level Agreements („SLA"), welche dem Bestellformular als Anlage beigefügt sind.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>3.3</strong> Aqua-Concept ist berechtigt, den Zugang zu C3 zeitweise zu sperren, wenn und solange dies aus berechtigtem
  Anlass geboten ist. Als berechtigter Anlass in diesem Sinne gilt insbesondere eine Bedrohung der Systemintegrität
  von C3 oder des Betriebs von Aqua-Concept sowie ein wesentlicher Verstoß von Nutzern gegen Bestimmungen
  dieses C3-Service-Vertrages.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  4. Laufzeit des C3-Service-Vertrages und Kündigung
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>4.1</strong> Soweit nicht im Bestellformular etwas anderes vereinbart ist, beginnt dieser C3-Service-Vertrag mit
  Vertragsunterzeichnung und hat eine Laufzeit von 12 Monaten („Vertragslaufzeit").
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>4.2</strong> Der Vertrag verlängert sich automatisch um jeweils ein Jahr, wenn er nicht von einer Partei unter Einhaltung einer
  Kündigungsfrist von 3 Monaten zum Ende der jeweiligen Vertragslaufzeit gekündigt wird.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>4.3</strong> Bei Verlängerung wird eine jährliche Preiserhöhung von jeweils 4% auf alle vereinbarten wiederkehrenden
  Preisbestandteile vereinbart, wenn nicht im Bestellformular etwas anderes vereinbart ist.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>4.4</strong> Das Recht zur fristlosen Kündigung aus wichtigem Grund bleibt unberührt.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>4.5</strong> Alle Kündigungen bedürfen zu ihrer Wirksamkeit der Schriftform.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  5. Technische und vertragliche Änderungen
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>5.1</strong> Aqua-Concept kann jederzeit technische oder operative Änderungen an C3 vornehmen, sofern es für diese
  Änderungen gute Gründe gibt, z.B., weil diese Änderungen aufgrund einer neuen technischen Umgebung,
  Änderungen der für die Serviceleistungen relevanten Gesetzeslage oder der Rechtsprechung (insbesondere mit
  Blick auf den Datenschutz, Haftung und Umweltrecht) inklusive gerichtlichen oder behördlichen Anordnungen,
  erhöhten Nutzerzahlen von C3 oder anderen wichtigen betriebstechnischen Gründen erforderlich sind.
  Aqua-Concept wird den Servicenehmer innerhalb einer angemessenen Frist vor dem Zeitpunkt der Änderung
  schriftlich über die Änderung und deren Wirksamwerden informieren.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>5.2</strong> Sofern die Änderungen gem. Abschnitt 5.1 Änderungen wesentlicher, insbesondere vertraglich zugesicherter
  Bestandteile oder wesentliche Verschlechterungen der geschuldeten Leistungen zur Folge haben oder aus
  anderen Gründen für den Servicenehmer unzumutbar sind und Aqua-Concept keine Weiterführung der
  geschuldeten Leistungen zu unveränderten Bedingungen anbietet, kann der Servicenehmer den C3-Service-
  Vertrag außerordentlich innerhalb von dreißig (30) Kalendertagen ab Zugang der Information durch Aqua-Concept
  mit Wirksamkeit zum Zeitpunkt der geplanten Änderung kündigen.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  6. Rechteeinräumung
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>6.1</strong> Aqua-Concept räumt dem Servicenehmer ein zeitlich auf die Dauer der Vertragslaufzeit und inhaltlich auf die
  bestimmungsgemäße Nutzung von C3, durch Berechtigte Nutzer und der über C3 bereitgestellten Ausgaben
  beschränktes einfaches Nutzungsrecht für eigene Geschäftszwecke ein. Untersagt ist insbesondere jegliche
  Nutzung von C3 und seiner Ausgaben im Zusammenhang mit der Entwicklung von Konkurrenzprodukten sowie
  die unbefugte Bereitstellung von C3 gegenüber Dritten.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>6.2</strong> Umgekehrt räumt der Servicenehmer Aqua-Concept für die Dauer der Vertragslaufzeit alle zum Zwecke der
  vertragsgemäßen Bereitstellung von C3 erforderlichen Nutzungsrechte an den Servicenehmerdaten ein.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  7. Gewährleistung
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>7.1</strong> Soweit auf den C3-Service-Vertrag Mietrecht Anwendung findet (insbesondere im Zusammenhang mit der
  Bereitstellung von C3), kann der Servicenehmer das Recht zur Mietminderung infolge eines Mangels nur ausüben,
  soweit das Minderungsrecht oder der zugrundeliegende Mangel rechtskräftig festgestellt ist. Die
  Minderungserklärung bedarf der Schriftform. Die Möglichkeit des Servicenehmers zur späteren Rückforderung des
  zu viel Geleisteten bleibt hiervon unberührt.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>7.2</strong> Der Servicenehmer trägt die Beweislast dafür, dass fehlerhafte Ausgaben auf einem Mangel von C3 beruhen. Ein
  Mangel liegt nur dann vor, wenn Aqua-Concept eine Fehlfunktion trotz Erkennbarkeit nach Stand der Technik
  nicht verhindert oder den Servicenehmer nicht auf sie hingewiesen hat.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>7.3</strong> Im Übrigen gilt das gesetzliche Gewährleistungsrecht.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  8. Mitwirkungspflichten des Servicenehmers
</Typography>

<Typography variant="body2" gutterBottom>
  Zur vertragsgemäßen Bereitstellung von C3 und um die Potenziale von C3 für den Servicenehmer optimal
  auszuschöpfen, ist Aqua-Concept auf die Mitwirkung des Servicenehmers angewiesen. Der Servicenehmer sichert
  Aqua-Concept daher die Erbringung folgender Mitwirkungsleistungen zu:
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>8.1</strong> Ist für die Bereitstellung von C3 der Zugang zu Systemen beim Servicenehmer oder bei für den Servicenehmer
  tätigen Dritten erforderlich (z.B. zur Installation von Softwarekomponenten oder Schnittstellen), wird der
  Servicenehmer den erforderlichen physischen Zugang oder Fernzugang zu den Systemen rechtzeitig gewähren
  bzw. sicherstellen.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>8.2</strong> Dem Servicenehmer ist bekannt und er ist damit einverstanden, dass die vertragsgemäße Installation, Einrichtung,
  Betrieb und/oder Verwendung von C3 von der Erfüllung bestimmter Anforderungen an die System- und
  Netzwerkumgebung des Servicenehmers abhängen. Der Servicenehmer ist dafür verantwortlich, einen geeigneten
  Internetzugang sowie die ggf. in einer Anlage zu dem Bestellformular spezifizierten anderen Systemanforderungen
  für C3 vorzuhalten.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>8.3</strong> Der Servicenehmer stellt sicher, dass Login-Zugriffe auf und Eingaben in C3 nur von Berechtigten Dritten erfolgen.
  Der Servicenehmer wird C3 Dritten nur unter den Bedingungen dieses C3-Service-Vertrages zugänglich machen.
  Der Servicenehmer wird etwaige Zugangsdaten zu C3 geheim halten und vor unbefugtem Zugang Dritter
  schützen. Bei Verlust oder Offenlegung der Zugangsdaten wird der Servicenehmer Aqua-Concept unverzüglich
  benachrichtigen.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>8.4</strong> Der Servicenehmer wird C3 vor seiner operativen Nutzung angemessen auf Verwendbarkeit und Eignung für seine
  Zwecke testen und die operative Nutzung nur bei positivem Testergebnis beginnen. Er prüft insbesondere, welche
  für ihn relevanten Grenzwerte in der Plattform abgebildet sind, so dass für Überschreitungen Warnungen
  ausgegeben werden, und stellt die Überwachung nicht abgebildeter relevanter Grenzwerte anderweitig auf
  eigene Verantwortung sicher.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>8.5</strong> Der Servicenehmer speichert die Servicenehmerdaten und andere möglicherweise betroffene Daten vor
  Inbetriebnahme von C3 und anschließend in angemessenen Abständen, zumindest jedoch kalenderwöchentlich
  ordnungsgemäß, auf eigene Verantwortung und Kosten sowie vollständig nach dem aktuellen Stand der Technik.
  Aqua-Concept darf darauf vertrauen, dass der Servicenehmer entsprechende Daten-Back-Ups und andere
  zumutbare Wiederherstellungsmaßnahmen zur Sicherung seiner Datenbestände vorhält.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>8.6</strong> Der Servicenehmer verpflichtet sich, Aqua-Concept die für die ordnungsgemäße Bereitstellung von C3 (insb.
  Wasserdaten und sonstigen Daten der betrieblichen Einrichtungen) zur Verfügung zu stellen, soweit diese Daten
  zur Erbringung der Serviceleistung von Bedeutung sein können. Sie sind vom Servicenehmer außerdem in einem
  Anlagenstammblatt zu erfassen und für die Dauer der Vertragslaufzeit aufzubewahren.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>8.7</strong> Der Servicenehmer stellt sicher, dass die Servicenehmerdaten keine Beschädigungen von C3 oder des Betriebs
  der Aqua-Concept verursachen, etwa durch Viren oder Würmer. Ferner ist der Servicenehmer verpflichtet,
  Servicenehmerdaten, sofern vorhanden, nur im Einklang mit den Bedienungs- oder Nutzeranleitungen oder
  sonstigen Anweisungen von Aqua-Concept in C3 einzugeben. Eingabefehler von Nutzern aus der Sphäre des
  Servicenehmers oder sonstige Fehler bei der Eingabe von Servicenehmerdaten außerhalb des Machtbereichs von
  Aqua-Concept gehen nicht zu Lasten von Aqua-Concept.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>8.8</strong> Der Servicenehmer verpflichtet Berechtigte Nutzer, denen er Zugang zu C3 gewährt, auf die Einhaltung der
  Bestimmungen dieses C3-Service-Vertrages.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>8.9</strong> Der Servicenehmer lässt sich gegenüber Dritten, die Rechte an den Servicenehmerdaten halten, gegebenenfalls
  alle zur vertragsgemäßen Bereitstellung von C3 erforderlichen Nutzungsrechte an diesen Servicenehmerdaten
  unterlizenzierbar einräumen.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  9. Garantie und Freistellung durch den Servicenehmer
</Typography>

<Typography variant="body2" gutterBottom>
  Der Servicenehmer garantiert, dass die Nutzung der Servicenehmerdaten durch Aqua-Concept im Rahmen der
  Vertragsdurchführung keine Rechte Dritter verletzt oder gegen anwendbare Gesetze verstößt, insbesondere nicht
  gegen den Datenschutz, Geheimhaltungsvorgaben und Berufsgeheimnisse. Wird Aqua-Concept durch einen
  Dritten wegen einer Rechteverletzung durch die vertragsgemäße Nutzung der Servicenehmerdaten in Anspruch
  genommen, stellt der Servicenehmer Aqua-Concept auf eigene Kosten und auf erstes Anfordern von diesen
  Ansprüchen frei. Dies umfasst auch die notwendigen Kosten der Rechtsverteidigung. Weitergehende Ansprüche
  von Aqua-Concept bleiben unberührt.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  10. Datenexport
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>10.1</strong> Aqua-Concept stellt dem Servicenehmer ein (1) Mal pro Kalenderjahr, in der Regel bis zum 31. Dezember eines
  jeden Jahres, einen Jahresbericht über die in dem jeweiligen Jahr in C3 eingegebenen Servicenehmerdaten,
  insbesondere der Wasserdaten und generierten Ausgaben, im Portable Document Format („PDF") bereit.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>10.2</strong> Mit Ende der Vertragslaufzeit, sofern keine automatische Verlängerung eingetreten ist, oder sonstiger Beendigung
  des Vertrages wird Aqua-Concept auf Anfrage des Servicenehmers diesem innerhalb von einem (1)
  Kalendermonat eine Historie aller Jahresberichte der Vertragslaufzeit zum Abruf als PDF zur Verfügung stellen. Eine
  Aufbereitung von Servicenehmerdaten durch Aqua Concept ist nicht geschuldet.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>10.3</strong> Aqua-Concept ist berechtigt, die Servicenehmerdaten sowie alle Jahresberichte nach Ablauf von einem (1)
  Kalendermonat nach Beendigung des Vertrages zu löschen.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>10.4</strong> Darüber hinaus schuldet Aqua Concept keine Unterstützungsleistungen nach Beendigung des Vertrages. Die
  Parteien können solche gesondert gegen Vergütung vereinbaren.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  11. Haftung
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>11.1</strong> Soweit sich aus diesem C3-Service-Vertrag nichts anderes ergibt, haftet Aqua Concept bei einer Verletzung von
  vertraglichen und außervertraglichen Pflichten nach den einschlägigen gesetzlichen Vorschriften.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>11.2</strong> Auf Schadensersatz haftet Aqua Concept – gleich aus welchem Rechtsgrund – bei Vorsatz und grober
  Fahrlässigkeit.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>11.3</strong> Bei einfacher Fahrlässigkeit haftet Aqua Concept nur
</Typography>

<Typography variant="body2" gutterBottom sx={{ pl: "16px" }}>
  a. für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit,
</Typography>

<Typography variant="body2" gutterBottom sx={{ pl: "16px" }}>
  b. für Schäden aus der Verletzung einer wesentlichen Vertragspflicht (Verpflichtung, deren Erfüllung die
  ordnungsgemäße Durchführung des Vertrags überhaupt erst ermöglicht und auf deren Einhaltung der
  Vertragspartner regelmäßig vertraut und vertrauen darf); in diesem Fall ist die Haftung jedoch auf den
  Ersatz des vorhersehbaren, typischerweise eintretenden Schadens begrenzt.
</Typography>

<Typography variant="body2" gutterBottom>
  Im Übrigen ist eine Haftung bei einfacher Fahrlässigkeit ausgeschlossen.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>11.4</strong> Soweit auf den C3-Service-Vertrag Mietrecht Anwendung findet (insbesondere in Bezug auf die Bereitstellung von
  C3), ist die verschuldensunabhängige Haftung von Aqua-Concept nach § 536a Abs. 1 Alt. 1 BGB ausgeschlossen.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>11.5</strong> Aqua-Concept haftet für Schäden nur, wenn und soweit diese nicht auf einer Verletzung von Mitwirkungspflichten
  durch den Servicenehmer beruhen, insbesondere, aber nicht nur, der Verpflichtung des Servicenehmers zur
  Datensicherung gemäß Ziffer 8.5 und / oder zur korrekten Dateneingabe gemäß Ziffer 8.6.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>11.6</strong> C3 ist so konfiguriert, dass C3 im Falle von Überschreitungen voreingestellter Grenzwerte Warnungen ausgibt. Der
  Servicenehmer ist verpflichtet in eigener Verantwortung zu prüfen, ob diese Grenzwerte für ihn relevant sind und
  welche für ihn relevanten Grenzwerte nicht in C3 abgebildet sind. Aqua Concept haftet nicht für Schäden, die
  daraus entstehen, dass für nicht in C3 abgebildete Grenzwerte keine Warnung(en) ausgegeben wurden.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>11.7</strong> Aqua Concept obliegt es, zur Abdeckung von Schadensersatzansprüchen eine eigene Haftpflichtversicherung zu
  unterhalten.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  12. Geheimhaltung
</Typography>

<Typography variant="body2" gutterBottom>
  Die Parteien verpflichten sich, alle vertraulichen Informationen, die ihnen bei der Durchführung dieses Vertrags
  bekannt werden, vertraulich zu behandeln und nur für vertraglich vereinbarte Zwecke zu verwenden. Vertrauliche
  Informationen im Sinne dieser Bestimmung sind Informationen, Unterlagen, Angaben und Daten, die als solche
  bezeichnet sind oder ihrer Natur nach als vertraulich anzusehen sind.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  13. Datenschutz
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>13.1</strong> Die Parteien verpflichten sich, die Bestimmungen des Datenschutzes, insbesondere der
  Datenschutzgrundverordnung (DSGVO) und des Bundesdatenschutzgesetzes (BDSG) einzuhalten.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>13.2</strong> Soweit durch die Beauftragung der Aqua-Concept ein Auftragsverarbeitungsverhältnis im Sinne des Art. 28 DSGVO
  oder eine gemeinsame Verantwortlichkeit nach Art. 26 DSGVO entsteht, schließen die Parteien eine gesonderte
  Vereinbarung über die Datenverarbeitung im Auftrag bzw. die gemeinsame Verantwortlichkeit, die als Anlage
  zum Bestellformular Bestandteil dieses C3-Service-Vertrages ist und allen anderen vertraglichen Regelungen
  zwischen den Parteien vorgeht.
</Typography>

<Typography variant="h6" gutterBottom fontWeight="bold" mt="16px">
  14. Schlussbestimmungen
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>14.1</strong> Zur Wirksamkeit der in diesem C3-Service-Vertrag vereinbarten Schriftform genügt die Textform, es sei denn, die
  Textform ist im Einzelfall ausdrücklich ausgeschlossen.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>14.2</strong> Der Servicenehmer ist zur Aufrechnung oder Zurückbehaltung, auch wenn Gegenansprüche geltend gemacht
  werden, nur berechtigt, wenn die Gegenansprüche unstreitig oder rechtskräftig festgestellt worden sind.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>14.3</strong> Auf diesen C3-Service-Vertrag und sämtliche Rechtsbeziehungen zwischen Aqua Concept und dem
  Servicenehmer findet ausschließlich das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-
  Kaufrechts Anwendung. Ausschließlicher – auch internationaler – Gerichtsstand für alle Streitigkeiten aus diesem
  C3-Service-Vertrag ist München, soweit nicht ein ausschließlicher gesetzlicher Gerichtsstand besteht.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>14.4</strong> Änderungen des C3-Service-Vertrages und Nebenabsprachen bedürfen der Schriftform. Das gilt insbesondere
  auch für die Änderung dieses Schriftformerfordernisses selbst.
</Typography>

<Typography variant="body2" gutterBottom>
  <strong>14.5</strong> Sollte eine Bestimmung dieses C3-Service-Vertrages unwirksam oder undurchführbar sein oder werden oder sollte
  sich eine Vertragslücke herausstellen, so wird hierdurch die Wirksamkeit der übrigen Bestimmungen dieser
  Vereinbarung nicht berührt. Die Parteien sind sich darüber einig, dass diese Weitergeltungsregelung keine bloße
  Beweislastumkehr darstellt, sondern dass eine Teilnichtigkeit des C3-Service Vertrages auch ohne die unwirksame
  Bestimmung nicht vorliegt. Die Parteien verpflichten sich in diesem Fall, die unwirksame oder undurchführbare
  Bestimmung durch eine wirksame und durchführbare Regelung zu ersetzen, die der unwirksamen oder
  undurchführbaren Bestimmung wirtschaftlich am nächsten kommt, bzw. eine Vertragslücke durch eine
  Bestimmung auszufüllen, die sie nach ihren wirtschaftlichen Absichten vereinbart hätten, wenn sie diesen
  regelungsbedürftigen Punkt bedacht hätten.
</Typography>
                </Paper>

                {/* Hint text shown until user has scrolled to the bottom */}
                {!agbScrolled && (
                  <Typography variant="caption" color="text.secondary" mt="4px" display="block">
                    Bitte scrollen Sie bis zum Ende, um die Bedingungen zu akzeptieren.
                  </Typography>
                )}

                <Divider sx={{ mt: "12px" }} />

                {/* Checkbox disabled until AGB text has been fully scrolled */}
                <FormControlLabel
                  sx={{ mt: "8px" }}
                  control={
                   <Checkbox
                    name="agbAccepted"
                    checked={values.agbAccepted}
                    onChange={handleChange}
                    disabled={!agbScrolled}
                    sx={{
                      '&.Mui-checked': {
                        color: 'grey',
                      },
                    }}
                />
                  }
                  label="Ich habe die AGB gelesen und akzeptiere diese."
                />
              </Box>
            </Grid>

          </Grid>
        </form>
      )}
    </Formik>
  );
};

export default SetPasswordForm;